<template>
  <div
    class="board-navigation-pager"
    :class="{
      'board-navigation-pager--background-light': backgroundTheme === 'light',
      'board-navigation-pager--background-dark': backgroundTheme === 'dark',
    }"
  >
    <button
      :aria-label="draftType === 'board' ? $t('Previous page') : $t('Previous slide')"
      class="board-navigation-pager__button-left nebula-button nebula-button--s
        nebula-button--pill nebula-button--fill nebula-button--icon-left
        nebula-button--icon-only"
      :class="{
        'nebula-button--ghost': backgroundTheme === 'light',
      }"
      :disabled="disabled || currentPageIndex === 0"
      @click="goToPrevPage"
    >
      <NebulaIcon
        symbol-id="caret-left"
        class-override="nebula-button__icon"
      />
    </button>
    <button
      v-if="expandable"
      class="board-navigation-pager__expand-button nebula-button
        nebula-button--ghost nebula-button--s"
      aria-describedby="board-navigation-pager__aria-description"
      :aria-label="$t('Open page drawer')"
      :disabled="disabled"
      @click="$emit('expand-clicked')"
    >
      <span
        aria-live="polite"
        id="board-navigation-pager__aria-description"
      >
        {{ pageNumberMessage }}
      </span>
      <NebulaIcon
        :symbol-id="expanded
          ? expandsDown ? 'caret-up' : 'caret-down'
          : expandsDown ? 'caret-down' : 'caret-up'"
        class-override="nebula-button__icon"
      />
    </button>
    <!-- eslint-disable-next-line -->
    <span v-else aria-live="polite" class="board-navigation-pager__page-number">{{ pageNumberMessage }}</span>
    <button
      :aria-label="draftType === 'board' ? $t('Next page') : $t('Next slide')"
      class="board-navigation-pager__button-right nebula-button nebula-button--s
        nebula-button--pill nebula-button--fill nebula-button--icon-left
        nebula-button--icon-only"
      :class="{
        'nebula-button--ghost': backgroundTheme === 'light',
      }"
      :disabled="disabled || currentPageIndex === draft.pages.length - 1"
      @click="goToNextPage"
    >
      <NebulaIcon
        symbol-id="caret-right"
        class-override="nebula-button__icon"
      />
    </button>
  </div>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { mapActions, mapState } from 'pinia';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { bus } from '@/lib/eventBus';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  components: {
    NebulaIcon,
  },
  props: {
    expandable: {
      type: Boolean,
      default: false,
    },
    expandsDown: {
      type: Boolean,
      default: false,
    },
    backgroundTheme: {
      type: String,
      default: 'dark', // dark, light
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['expand-clicked'],
  data() {
    return {
      nebulaMobilePortrait: parseInt(nebulaTokens.breakpoints['mobile-portrait'], 10),
      nebulaTabletLandscape: parseInt(nebulaTokens.breakpoints['tablet-landscape'], 10),
      windowWidth: Infinity,
    };
  },
  mounted() {
    bus.on('window:resize', this.onWindowResize);
    this.onWindowResize();
  },
  beforeUnmount() {
    bus.off('window:resize', this.onWindowResize);
  },
  computed: {
    ...mapState(useEditorStore, [
      'draft',
      'currentPageIndex',
      'draftType',
    ]),
    pageNumberMessage() {
      const totalPages = this.draft.pages.length;
      const l10nOpts = {
        currentPage: this.currentPageIndex + 1,
        totalPages,
      };

      if (this.windowWidth < this.nebulaMobilePortrait) {
        return l10nOpts.currentPage;
      }

      if (this.windowWidth < this.nebulaTabletLandscape) {
        return this.$t('%(currentPage)s of %(totalPages)s', l10nOpts);
      }

      if (this.draftType === 'board') {
        return this.$t('Page %(currentPage)s of %(totalPages)s', l10nOpts);
      }

      return this.$t('Slide %(currentPage)s of %(totalPages)s', l10nOpts);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
    ]),
    onWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    goToNextPage() {
      this[types.VIEW_NEXT_PAGE]();
    },
    goToPrevPage() {
      this[types.VIEW_PREV_PAGE]();
    },
  },
};
</script>

<style lang="stylus">
.board-navigation-pager {
  align-items: center;
  display: flex;

  &__button-left {
    margin-inline-end: $nebula-space-1x;
  }

  &__button-right {
    margin-inline-start: $nebula-space-1x;
  }

  &__expand-button {
    background: transparent;
    border-width: 0;
    color: $nebula-color-white;

    &:hover,
    &:focus {
      background: $nebula-color-interactive-blue-300;
    }

    svg.nebula-button__icon {
      fill: $nebula-color-white;
      margin-inline-start: $nebula-space-1x;
    }
  }

  &--background-dark {
    &__button-left, &__button-right {
      &:disabled {
        /*
          Not using the default disabled styles since that makes this
          button semi-transparent on top of a semi-transparent background
          which causes underlying images to show through on the button. Using
          a custom color to try to simulate the nebula disabled look.
        */
        background: #7BA3C2;
      }
    }
  }

  &--background-light {
    .board-navigation-pager__expand-button {
      color: $nebula-color-interactive-blue-300;

      &:hover,
      &:focus {
        background: $nebula-color-platform-interactive-200;
      }

      &:active {
        background: $nebula-color-platform-interactive-400;
      }

      svg.nebula-button__icon {
        fill: $nebula-color-interactive-blue-300;
      }
    }

    .board-navigation-pager__page-number {
      color: $nebula-color-interactive-blue-300;
      font-size: $nebula-font-size-body-2;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  @media (max-width: ($nebula-breakpoints-mobile-portrait - 1px)) {
    // Remove margin on very small screens
    &__button-right.nebula-button,
    &__button-left.nebula-button {
      margin: 0;
    }
  }

  @media (max-width: ($nebula-breakpoints-mobile-landscape - 1px)) {
    // Adjust padding on very small screens.
    &__expand-button.nebula-button {
      --nebula-button-padding: 7px $nebula-space-1x;
    }
  }
}
</style>
